import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, WhatsappLogo, EnvelopeSimple, MapPin, PaperPlaneRight, FacebookLogo } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/context/AuthContext";
import { SITE, SERVICES } from "@/constants/site";

const inputCls = "w-full bg-sage border border-reed rounded-xl px-4 py-3.5 text-sm text-ink placeholder:text-moss/60 focus:outline-none focus:ring-2 focus:ring-mint/50 focus:border-mint transition-shadow duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: SERVICES[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, email: form.email || null });
      toast.success("Enquiry sent! We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", service: SERVICES[0], message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-forest text-white relative overflow-hidden" data-testid="contact-section">
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-forest-soft/50 blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-mint-soft">Get in touch</p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-balance">
              Let's secure your tomorrow, today
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed max-w-md">
              Share a few details and we'll call you back with clear options — no obligation,
              no pressure, no jargon.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-col gap-4">
            <Reveal delay={0.1}>
              <motion.a href={`tel:${SITE.phoneHref}`} data-testid="contact-call-button" whileTap={{ scale: 0.98 }}
                className="flex items-center gap-4 bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 hover:bg-white/15 transition-colors duration-300">
                <span className="w-11 h-11 rounded-full bg-mint text-forest-deep flex items-center justify-center"><Phone size={20} weight="fill" /></span>
                <span><span className="block text-xs text-white/60">Call now</span><span className="block font-semibold">{SITE.phoneDisplay}</span></span>
              </motion.a>
            </Reveal>
            <Reveal delay={0.18}>
              <motion.a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi, I'd like a quote.")}`} target="_blank" rel="noopener noreferrer"
                data-testid="contact-whatsapp-button" whileTap={{ scale: 0.98 }}
                className="flex items-center gap-4 bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 hover:bg-white/15 transition-colors duration-300">
                <span className="w-11 h-11 rounded-full bg-mint text-forest-deep flex items-center justify-center"><WhatsappLogo size={20} weight="fill" /></span>
                <span><span className="block text-xs text-white/60">WhatsApp us</span><span className="block font-semibold">Chat instantly</span></span>
              </motion.a>
            </Reveal>
            <Reveal delay={0.26}>
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-link"
                className="flex items-center gap-4 px-5 py-4 text-white/70 text-sm hover:text-white transition-colors duration-300">
                <EnvelopeSimple size={20} className="text-mint-soft shrink-0" /> {SITE.email}
              </a>
            </Reveal>
            <Reveal delay={0.3}>
              <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-link"
                className="flex items-center gap-4 px-5 py-4 text-white/70 text-sm hover:text-white transition-colors duration-300">
                <FacebookLogo size={20} className="text-mint-soft shrink-0" /> Follow Kris Fin Care on Facebook
              </a>
            </Reveal>
            <Reveal delay={0.34}>
              <div className="flex items-center gap-4 px-5 text-white/70 text-sm">
                <MapPin size={20} className="text-mint-soft shrink-0" /> {SITE.address}
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="enquiry-form"
            className="bg-white text-ink rounded-3xl p-8 md:p-12 shadow-2xl shadow-forest-deep/40">
            <h3 className="font-serif text-2xl md:text-3xl">Request a callback</h3>
            <p className="mt-2 text-sm text-moss">We usually respond within a few working hours.</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <input required value={form.name} onChange={set("name")} placeholder="Full name"
                className={inputCls} data-testid="enquiry-name-input" />
              <input required value={form.phone} onChange={set("phone")} placeholder="Phone number" type="tel"
                className={inputCls} data-testid="enquiry-phone-input" />
              <input value={form.email} onChange={set("email")} placeholder="Email (optional)" type="email"
                className={inputCls} data-testid="enquiry-email-input" />
              <select value={form.service} onChange={set("service")} className={inputCls} data-testid="enquiry-service-select">
                {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea value={form.message} onChange={set("message")} rows={4}
                placeholder="Tell us briefly what you need (optional)"
                className={`${inputCls} sm:col-span-2 resize-none`} data-testid="enquiry-message-input" />
            </div>

            <motion.button type="submit" disabled={sending} whileTap={{ scale: 0.98 }}
              data-testid="enquiry-submit-button"
              className="mt-8 w-full sm:w-auto bg-forest text-white font-semibold px-10 py-4 rounded-full hover:bg-forest-soft disabled:opacity-60 transition-colors duration-300 flex items-center justify-center gap-2">
              {sending ? "Sending..." : "Send Enquiry"} <PaperPlaneRight size={16} weight="bold" />
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
